import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IC, Svg } from '../art/icons.js';
import { useStore } from '../lib/store.js';
import { useNav } from '../lib/router.js';
import { useLook } from '../lib/look.js';

/**
 * Bottom sheet. With `footer`, the body scrolls on its own and the footer (buttons) stays pinned at the bottom. Drawn on
 * body, outside the app shell (Saif, 21 Oct): the shell clips and never scrolls, so a focused field can't shove the page up;
 * the sheet is sized to the visual viewport (--vvh/--vvt, the keyboard taking its share) and a focused field, Stripe's
 * included, is scrolled into view above the keyboard. The title stays pinned while the body scrolls.
 */
export function Sheet({ children, onClose, locked, footer }: { children: ReactNode; onClose: () => void; locked?: boolean; footer?: ReactNode }) {
  const panel = useRef<HTMLDivElement>(null);
  // The title's background only once it is pinned over scrolling content (at rest it is the panel itself, so no strip; Saif 22 Oct).
  useEffect(() => {
    const root = panel.current?.classList.contains('split') ? panel.current.querySelector('.sheet-body') : panel.current;
    const mark = root?.querySelector('.sheet-top');
    if (!root || !mark || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => panel.current?.toggleAttribute('data-stuck', !e?.isIntersecting), { root, threshold: 1 });
    io.observe(mark);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !locked) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, locked]);
  useEffect(() => {
    const vv = window.visualViewport;
    const html = document.documentElement;
    const reveal = () => {
      const a = document.activeElement;
      if (a instanceof HTMLElement && panel.current?.contains(a)) setTimeout(() => a.scrollIntoView({ block: 'center', behavior: 'smooth' }), 60);
    };
    const onSize = () => {
      if (vv) {
        html.style.setProperty('--vvh', `${Math.round(vv.height)}px`);
        html.style.setProperty('--vvt', `${Math.round(vv.offsetTop)}px`);
      }
      reveal();
    };
    vv?.addEventListener('resize', onSize);
    vv?.addEventListener('scroll', onSize);
    document.addEventListener('focusin', reveal);
    onSize();
    return () => {
      vv?.removeEventListener('resize', onSize);
      vv?.removeEventListener('scroll', onSize);
      document.removeEventListener('focusin', reveal);
      html.style.removeProperty('--vvh');
      html.style.removeProperty('--vvt');
    };
  }, []);
  // Drag down to close (Saif, 21 Oct evening: "I can't seem to exit that screen by dragging it down"). A downward drag that
  // starts on the handle or the title, or anywhere once the sheet's scrolling part is at its top, moves the panel with the
  // finger and closes it past 110px or on a quick flick; otherwise the touch scrolls as usual. Locked sheets don't move.
  // The listeners are native (touchmove must not be passive so the drag can stop the scroller bouncing underneath).
  const latest = useRef({ onClose, locked });
  latest.current = { onClose, locked };
  useEffect(() => {
    const el = panel.current;
    if (!el) return;
    let drag: { y: number; t: number; dy: number; on: boolean } | null = null;
    const scroller = (from: EventTarget | null): HTMLElement | null => {
      let n = from instanceof HTMLElement ? from : null;
      while (n && n !== el) {
        const s = getComputedStyle(n);
        if ((s.overflowY === 'auto' || s.overflowY === 'scroll') && n.scrollHeight > n.clientHeight) return n;
        n = n.parentElement;
      }
      return el.scrollHeight > el.clientHeight ? el : null;
    };
    const start = (e: TouchEvent) => {
      if (latest.current.locked || e.touches.length !== 1) { drag = null; return; }
      const t = e.target instanceof HTMLElement ? e.target : null;
      const onHandle = !!t && (t.classList.contains('grab') || !!t.closest('.h2'));
      const sc = scroller(e.target);
      drag = { y: e.touches[0]!.clientY, t: Date.now(), dy: 0, on: onHandle || !sc || sc.scrollTop <= 0 };
    };
    const move = (e: TouchEvent) => {
      if (!drag || !drag.on) return;
      const dy = e.touches[0]!.clientY - drag.y;
      if (dy <= 0) { drag.dy = 0; el.style.transform = ''; return; }
      drag.dy = dy;
      el.style.transition = 'none';
      el.style.transform = `translate3d(0, ${dy}px, 0)`;
      if (e.cancelable) e.preventDefault();
    };
    const end = () => {
      const d = drag;
      drag = null;
      if (!d || !d.on) return;
      const fast = d.dy > 40 && Date.now() - d.t < 260;
      el.style.transition = 'transform .22s ease-out';
      if (d.dy > 110 || fast) {
        el.style.transform = 'translate3d(0, 110%, 0)';
        setTimeout(() => latest.current.onClose(), 180);
      } else {
        el.style.transform = '';
      }
    };
    el.addEventListener('touchstart', start, { passive: true });
    el.addEventListener('touchmove', move, { passive: false });
    el.addEventListener('touchend', end);
    el.addEventListener('touchcancel', end);
    return () => {
      el.removeEventListener('touchstart', start);
      el.removeEventListener('touchmove', move);
      el.removeEventListener('touchend', end);
      el.removeEventListener('touchcancel', end);
    };
  }, []);
  return createPortal(
    <div id="sheet">
      <div className="scrim" onClick={() => !locked && onClose()}>
        <div className={`sheet-panel${footer !== undefined ? ' split' : ''}`} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} ref={panel}>
          <span className="grab" />
          {footer !== undefined ? <><div className="sheet-body"><i className="sheet-top" aria-hidden="true" />{children}</div><div className="sheet-foot">{footer}</div></> : <><i className="sheet-top" aria-hidden="true" />{children}</>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function Toast() {
  const { toastMsg, sheet } = useStore();
  if (!toastMsg) return null;
  // Over a sheet the toast moves to the top, so it never covers the sheet's buttons.
  return <div id="toast" className={`toast${sheet ? ' over-sheet' : ''}`} role="status">{toastMsg}</div>;
}

export function Notif() {
  const { notif, showNotif } = useStore();
  const { navigate } = useNav();
  if (!notif) return null;
  return (
    <button id="notif" className="notif" type="button" onClick={() => { showNotif(null); if (notif.orderId) navigate(`/order/${notif.orderId}`); }}>
      <span className="appicon"><img src="/logo.webp" alt="" /></span>
      <span className="grow col" style={{ gap: 2 }}><b style={{ fontSize: 13.5 }}>{notif.title}</b><span className="small soft">{notif.body}</span></span>
      <span className="small muted">now</span>
    </button>
  );
}

export function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return <button type="button" className="switch" role="switch" aria-checked={on} aria-label={label} onClick={() => onChange(!on)} />;
}

export function Round({ onClick, label, icon = IC.back, className = '' }: { onClick: () => void; label: string; icon?: string; className?: string }) {
  return <button type="button" className={`round ${className}`} onClick={onClick} aria-label={label}><Svg html={icon} /></button>;
}

export function TopBar({ title, onBack }: { title: string; onBack?: () => void }) {
  const { back } = useNav();
  return (
    <div className="topbar">
      <Round onClick={onBack ?? back} label="Back" />
      <h1 className="display" style={{ fontSize: 28 }}>{title}</h1>
    </div>
  );
}

/** Scrollable screen body; keeps the prototype's structure (#screen > .scroll > .pad). */
export function Scroll({ children, className = '', padStyle, id }: { children: ReactNode; className?: string; padStyle?: React.CSSProperties; id?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div className={`scroll ${className}`} ref={ref} id={id}>
      <div className="pad" style={padStyle}>{children}</div>
    </div>
  );
}

export function Spinner() {
  return <span className="loader" aria-hidden="true" />;
}

/** Online ordering is switched off: the menu and prices stay, this explains why there is no checkout. */
export function OrderingClosed({ compact }: { compact?: boolean }) {
  const s = useStore();
  const { t } = useLook();
  if (s.config?.ordering?.online !== false) return null;
  const title = t('closed.title');
  return (
    <div className="card ordering-closed" role="status" style={{ padding: compact ? '12px 14px' : 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
      <b style={{ fontSize: compact ? 14 : 16 }}>{title}</b>
      {/* The server's own reason wins over the admin's second line (docs/26 §6). */}
      <span className="small muted">{s.config.ordering.message ? s.config.ordering.message.replace(/^Online ordering opens soon\.\s*/, '') : t('closed.body')}</span>
    </div>
  );
}
